import { Space, Button } from "@mantine/core";
import { useMutation, useQuery } from "@apollo/client";
import { useForm } from "react-hook-form";
import { notifications } from "@mantine/notifications";

import { STYLE } from "../../types/constants";
import { UPDATE_ITEM_BILLING } from "../../gql/itemBilling";
import { GET_ALL_MATERIAL_TRACEABILITY_WHERE } from "../../gql/materialTraceability";
import { ModelTypenames } from "../../types/enums";

import UpdateItemMaterialBrandAndLotNumberComponent from "./UpdateItemMaterialBrandAndLotNumberComponent";

const UpdateMaterialAndLotModal = ({
  order,
  closeModal,
  refetch,
  user,
  allMaterialTraceability,
}) => {
  const [updateItemBilling] = useMutation(UPDATE_ITEM_BILLING);

  const {
    formState: { errors },
    handleSubmit,
    control,
    watch,
    getValues,
  } = useForm({
    defaultValues: {
      ...(order.products ?? []).reduce((acc, product) => {
        (product?.anatomyItem ?? []).forEach(item => {
          acc[`${item.id}.lotNumber`] =
            item.itemBilling[0]?.firstMaterialLotNumber ?? "";
          acc[`${item.id}.materialBrand`] =
            item.itemBilling[0]?.firstMaterialTraceabilityId ?? "";
          acc[`${item.id}.secondMaterialLotNumber`] =
            item.itemBilling[0]?.secondMaterialLotNumber ?? "";
        });
        return acc;
      }, {}),
      ...(order.products ?? []).reduce((acc, product) => {
        (product?.removableItem ?? []).forEach(item => {
          acc[`${item.id}.lotNumber`] =
            item.itemBilling[0]?.firstMaterialLotNumber ?? "";
          acc[`${item.id}.materialBrand`] =
            item.itemBilling[0]?.firstMaterialTraceabilityId ?? "";
          acc[`${item.id}.secondMaterialLotNumber`] =
            item.itemBilling[0]?.secondMaterialLotNumber ?? "";
        });
        return acc;
      }, {}),
      ...(order.products ?? []).reduce((acc, product) => {
        (product?.implantItem ?? []).forEach(item => {
          acc[`${item.id}.lotNumber`] =
            item.itemBilling[0]?.firstMaterialLotNumber ?? "";
          acc[`${item.id}.materialBrand`] =
            item.itemBilling[0]?.firstMaterialTraceabilityId ?? "";
          acc[`${item.id}.secondMaterialLotNumber`] =
            item.itemBilling[0]?.secondMaterialLotNumber ?? "";
        });
        return acc;
      }, {}),
    },
  });

  const updateItemById = async (values, item, modelTypenames) => {
    const selectedMaterialTraceability = (allMaterialTraceability ?? []).find(
      materialTraceability => {
        return materialTraceability.id === values[item.id].materialBrand;
      }
    );
    const itemBillingToUpdate = {
      variables: {
        data: {
          firstMaterialTraceability: selectedMaterialTraceability.id
            ? {
                connect: {
                  id: selectedMaterialTraceability.id,
                },
              }
            : undefined,
          firstMaterialLotNumber: {
            set: values[item.id].lotNumber,
          },
          firstMaterialReference:
            selectedMaterialTraceability.firstMaterialReference
              ? {
                  set: selectedMaterialTraceability.firstMaterialReference,
                }
              : undefined,
          firstMaterialBrand: selectedMaterialTraceability.firstMaterialBrand
            ? {
                set: selectedMaterialTraceability.firstMaterialBrand,
              }
            : undefined,
          firstMaterialApprovalReference:
            selectedMaterialTraceability.firstMaterialApprovalReference
              ? {
                  set: selectedMaterialTraceability.firstMaterialApprovalReference,
                }
              : undefined,
          firstMaterialCountry:
            selectedMaterialTraceability.firstMaterialCountry
              ? {
                  set: selectedMaterialTraceability.firstMaterialCountry,
                }
              : undefined,
          secondMaterialLotNumber: values[item.id].secondMaterialLotNumber
            ? {
                set: values[item.id].secondMaterialLotNumber,
              }
            : undefined,
          secondMaterialReference:
            selectedMaterialTraceability.secondMaterialReference
              ? {
                  set: selectedMaterialTraceability.secondMaterialReference,
                }
              : undefined,
          secondMaterialBrand: selectedMaterialTraceability.secondMaterialBrand
            ? {
                set: selectedMaterialTraceability.secondMaterialBrand,
              }
            : undefined,
          secondMaterialApprovalReference:
            selectedMaterialTraceability.secondMaterialApprovalReference
              ? {
                  set: selectedMaterialTraceability.secondMaterialApprovalReference,
                }
              : undefined,
          secondMaterialCountry:
            selectedMaterialTraceability.secondMaterialCountry
              ? {
                  set: selectedMaterialTraceability.secondMaterialCountry,
                }
              : undefined,
        },
        where: {
          id: item.itemBilling[0].id,
        },
      },
    };
    switch (modelTypenames) {
      case ModelTypenames.AnatomyItem:
        await updateItemBilling(itemBillingToUpdate);
        break;
      case ModelTypenames.RemovableItem:
        await updateItemBilling(itemBillingToUpdate);
        break;
      case ModelTypenames.ImplantItem:
        await updateItemBilling(itemBillingToUpdate);
        break;
    }
  };

  const onSubmit = async values => {
    try {
      (order?.products ?? []).forEach(async product => {
        Promise.all(
          (product?.anatomyItem ?? []).map(async item => {
            await updateItemById(values, item, ModelTypenames.AnatomyItem);
          })
        );
        Promise.all(
          (product?.removableItem ?? []).map(async item => {
            await updateItemById(values, item, ModelTypenames.RemovableItem);
          })
        );
        Promise.all(
          (product?.implantItem ?? []).map(async item => {
            await updateItemById(values, item, ModelTypenames.ImplantItem);
          })
        );
      });
      refetch();
      notifications.show({
        title: "Correctly saved",
        color: "green",
        message: "",
      });
      closeModal();
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to save lot number or material brand",
        color: "red",
        message: "",
      });
    }
  };

  return (
    <>
      {(order?.products ?? []).map(product => (
        <div key={product.id}>
          {(product?.anatomyItem ?? []).map(item => (
            <UpdateItemMaterialBrandAndLotNumberComponent
              key={item.id}
              item={item}
              product={product}
              errors={errors}
              control={control}
              watch={watch}
              getValues={getValues}
              allMaterialTraceability={allMaterialTraceability}
            />
          ))}
          {(product?.removableItem ?? []).map(item => (
            <UpdateItemMaterialBrandAndLotNumberComponent
              key={item.id}
              item={item}
              product={product}
              errors={errors}
              control={control}
              watch={watch}
              getValues={getValues}
              allMaterialTraceability={allMaterialTraceability}
            />
          ))}
          {(product?.implantItem ?? []).map(item => (
            <UpdateItemMaterialBrandAndLotNumberComponent
              key={item.id}
              item={item}
              product={product}
              errors={errors}
              control={control}
              watch={watch}
              getValues={getValues}
              allMaterialTraceability={allMaterialTraceability}
            />
          ))}
        </div>
      ))}
      <Space h="md" />
      <div style={{ textAlign: "center" }}>
        <Button
          style={{ backgroundColor: STYLE.primary }}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default UpdateMaterialAndLotModal;
